/**
 * Opening a file a record holds (a `file` fact or column, a timeline event's file, a dry run's file): which files open
 * in a new tab and which are saved, and what the person reads when one cannot be fetched. Pure: the shell's
 * `FileLinks` fetches the file with the person's sign-in and hands it to the capability ports.
 */
import { ApiError } from "../../data/api/errors";

const VIEWABLE_EXTENSIONS = /\.(pdf|png|jpe?g|gif|webp|bmp|avif)$/i;

/**
 * Whether a fetched file opens in a new tab (a PDF or an image, which a browser shows) rather than being saved. The
 * served media type decides; a server that answers with a generic type falls back to the file's name. An SVG is
 * saved, never opened: a tab opened from the fetched bytes shares One's origin, and an SVG can carry script.
 */
export function opensInNewTab(mediaType: string, name: string): boolean {
  const type = mediaType.split(";")[0]!.trim().toLowerCase();
  if (type === "image/svg+xml") return false;
  if (type === "application/pdf" || type.startsWith("image/")) return true;
  if (type !== "" && type !== "application/octet-stream") return false;
  return VIEWABLE_EXTENSIONS.test(name);
}

/** What the person reads when a file cannot be fetched: refused, gone, or not reached (no answer at all). */
export function fileOpenFailure(error: unknown, name: string): string {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403)
      return `You don't have access to ${name}.`;
    if (error.status === 404 || error.status === 410)
      return `${name} can't be found.`;
    return `${name} couldn't be opened. Try again.`;
  }
  return `${name} couldn't be opened. Check your connection and try again.`;
}
